// Analytics dashboard — editor-only rollup of `analytics_events`. One
// endpoint returns everything the page shows so pageview cost stays low.
//
// Panels:
//   - Events by kind (bar chart, sparklines)
//   - Daily activity (sparkline)
//   - Top-viewed features / releases (linked)
//   - Orphaned drafts — draft features with no activity in window
//   - Recent events (raw tail, last 50)
//
// Data isn't real-time; the underlying table is append-only so cached
// query is fine. Manual "days" switcher (7 / 30 / 90) reruns the query.
import { useState } from "react";
import { Link } from "wouter";
import type { AnalyticsDashboard, Persona } from "@release-hub/shared";
import { useAnalyticsDashboard } from "@/api/queries";
import { PERSONA_LABEL } from "@/auth/Persona";
import { LoadingLine } from "@/components/LoadingLine";
import { PageHint } from "@/components/PageHint";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { cn } from "@/lib/cn";

/** Display label for a persona identifier stored on an analytics event.
 *  Falls back to the raw value for legacy/unknown strings (the column is
 *  free-form TEXT and predates the current persona set). */
function personaLabel(p: string): string {
  return PERSONA_LABEL[p as Persona] ?? p;
}

const WINDOWS: { days: number; label: string }[] = [
  { days: 7, label: "Last 7 days" },
  { days: 30, label: "Last 30 days" },
  { days: 90, label: "Last 90 days" },
];

export function Analytics() {
  const [days, setDays] = useState(30);
  const { data, isPending, error } = useAnalyticsDashboard(days);

  return (
    <div className="space-y-6 py-6">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl">Analytics</h1>
          <PageHint>
            Where the hub is getting used. Reads{" "}
            <code className="rounded bg-border/40 px-1 font-mono text-xs">
              analytics_events
            </code>
            {" "}
            — every route change, export, and history open.
          </PageHint>
        </div>
        <WindowPicker days={days} onChange={setDays} />
      </header>

      {isPending ? (
        <LoadingLine />
      ) : error ? (
        <p className="text-sm text-destructive">
          Failed to load: {error instanceof Error ? error.message : "unknown"}
        </p>
      ) : data ? (
        <DashboardBody data={data} />
      ) : null}
    </div>
  );
}

function WindowPicker({
  days,
  onChange,
}: {
  days: number;
  onChange: (d: number) => void;
}) {
  return (
    <div className="inline-flex rounded-md border border-border-hover bg-bg p-0.5">
      {WINDOWS.map((w) => (
        <button
          key={w.days}
          type="button"
          onClick={() => onChange(w.days)}
          className={
            days === w.days
              ? "rounded-sm bg-card px-3 py-1 text-xs font-medium text-text shadow-sm"
              : "rounded-sm px-3 py-1 text-xs text-text-secondary hover:text-text"
          }
        >
          {w.label}
        </button>
      ))}
    </div>
  );
}

function DashboardBody({ data }: { data: AnalyticsDashboard }) {
  const totalEvents = data.by_event.reduce((s, r) => s + r.count, 0);
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <Panel title="Events by kind" hint={`${totalEvents} total in window`}>
        {data.by_event.length === 0 ? (
          <EmptyState>No events yet.</EmptyState>
        ) : (
          <BarList
            rows={data.by_event.map((r) => ({ label: r.event, count: r.count }))}
          />
        )}
      </Panel>

      <Panel title="Daily activity">
        {data.by_day.length === 0 ? (
          <EmptyState>Nothing tracked yet.</EmptyState>
        ) : (
          <DailySparkline rows={data.by_day} />
        )}
      </Panel>

      <Panel title="Most-viewed features">
        {data.top_features.length === 0 ? (
          <EmptyState>No feature views yet.</EmptyState>
        ) : (
          <ol className="space-y-1 text-sm">
            {data.top_features.map((r) => (
              <li key={r.entity_id} className="flex items-baseline justify-between gap-3">
                <Link
                  href={`/features/${r.entity_id}`}
                  className="min-w-0 flex-1 truncate text-text no-underline hover:text-accent"
                >
                  {r.entity_id}
                </Link>
                <span className="text-xs text-text-tertiary">{r.count}</span>
              </li>
            ))}
          </ol>
        )}
      </Panel>

      <Panel title="Most-viewed releases">
        {data.top_releases.length === 0 ? (
          <EmptyState>No release views yet.</EmptyState>
        ) : (
          <ol className="space-y-1 text-sm">
            {data.top_releases.map((r) => (
              <li key={r.entity_id} className="flex items-baseline justify-between gap-3">
                <Link
                  href={`/releases/${r.entity_id}`}
                  className="min-w-0 flex-1 truncate text-text no-underline hover:text-accent"
                >
                  {r.entity_id}
                </Link>
                <span className="text-xs text-text-tertiary">{r.count}</span>
              </li>
            ))}
          </ol>
        )}
      </Panel>

      <Panel
        title="Orphaned drafts"
        hint="Draft features nobody has viewed in this window"
      >
        {data.orphaned_drafts.length === 0 ? (
          <EmptyState>No orphans — every draft has activity.</EmptyState>
        ) : (
          <ul className="space-y-1 text-sm">
            {data.orphaned_drafts.map((d) => (
              <li key={d.id}>
                <Link
                  href={`/features/${d.id}`}
                  className="flex items-baseline justify-between gap-3 rounded px-2 py-1 no-underline hover:bg-border/40"
                >
                  <span className="min-w-0 flex-1 truncate text-text">
                    {d.name || d.id}
                  </span>
                  <span className="text-[11px] text-text-tertiary">
                    {d.owner_email?.split("@")[0] ?? "no owner"}
                    {d.first_seen_at ? ` · ${fmtDate(d.first_seen_at)}` : ""}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel title="Recent events" hint="Last 50, newest first" full>
        {data.recent_events.length === 0 ? (
          <EmptyState>No events yet.</EmptyState>
        ) : (
          <div className="max-h-[380px] overflow-y-auto rounded border border-border">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-card">
                <tr className="text-left text-text-tertiary">
                  <th className="border-b border-border px-2 py-1 font-semibold">
                    When
                  </th>
                  <th className="border-b border-border px-2 py-1 font-semibold">
                    Event
                  </th>
                  <th className="border-b border-border px-2 py-1 font-semibold">
                    Actor
                  </th>
                  <th className="border-b border-border px-2 py-1 font-semibold">
                    Persona
                  </th>
                  <th className="border-b border-border px-2 py-1 font-semibold">
                    Path / entity
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.recent_events.map((e) => (
                  <tr key={e.id} className="border-b border-border/60">
                    <td className="px-2 py-1 text-text-tertiary">
                      {e.created_at ? fmtDateTime(e.created_at) : "—"}
                    </td>
                    <td className="px-2 py-1 font-mono text-text">{e.event}</td>
                    <td className="px-2 py-1 text-text-secondary">
                      {e.actor_email?.split("@")[0] ?? "—"}
                    </td>
                    <td className="px-2 py-1 text-text-secondary">
                      {e.persona ? personaLabel(e.persona) : "—"}
                    </td>
                    <td className="px-2 py-1 text-text-secondary">
                      {e.entity_type && e.entity_id
                        ? `${e.entity_type}/${e.entity_id}`
                        : (e.path ?? "—")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}

function Panel({
  title,
  hint,
  full,
  children,
}: {
  title: string;
  hint?: string;
  full?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section
      className={cn(
        "rounded-lg border border-border bg-card p-5",
        full && "md:col-span-2",
      )}
    >
      <header className="mb-3">
        <h2 className="font-serif text-lg">{title}</h2>
        {hint && (
          <p className="mt-0.5 text-xs text-text-tertiary">{hint}</p>
        )}
      </header>
      {children}
    </section>
  );
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-md border border-dashed border-border bg-bg/40 px-3 py-4 text-center text-xs text-text-tertiary">
      {children}
    </p>
  );
}

/** Horizontal bar list — width proportional to the largest count.
 *  Good enough for small categorical rollups without a chart library. */
function BarList({ rows }: { rows: { label: string; count: number }[] }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <ul className="space-y-1.5 text-sm">
      {rows.map((r) => (
        <li key={r.label} className="grid grid-cols-[minmax(0,1fr)_2.5rem] items-center gap-2">
          <div className="min-w-0">
            <div className="flex items-baseline justify-between gap-2">
              <span className="truncate text-text">{r.label}</span>
            </div>
            <div className="mt-0.5 h-1.5 w-full rounded-full bg-border/40">
              <div
                className="h-full rounded-full bg-accent"
                style={{ width: `${(r.count / max) * 100}%` }}
              />
            </div>
          </div>
          <span className="text-right text-xs tabular-nums text-text-tertiary">
            {r.count}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Vertical bar sparkline. One bar per day; height proportional. Days
 *  with no events show as a hairline so gaps are visible. */
function DailySparkline({ rows }: { rows: { day: string; count: number }[] }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="flex items-end gap-0.5 h-24">
      {rows.map((r) => {
        const pct = (r.count / max) * 100;
        return (
          <div
            key={r.day}
            title={`${r.day} · ${r.count}`}
            className="flex-1 rounded-t bg-accent/70"
            style={{ height: `${Math.max(pct, 2)}%` }}
          />
        );
      })}
    </div>
  );
}
